import { useEffect, useRef } from 'react'

/** The first wait before re-reading a record whose stream has ended; each re-read doubles it. */
const SETTLING_REREAD_MS = 1_000

/**
 * A stream's terminal event can reach the page before the platform records what it followed as finished,
 * so once `isSettling`, `onSettle` runs and the record is re-read, backing off, until the wait reaches
 * `maxMs`: the record's own poll interval, which keeps re-reading it from then on.
 */
export const useRereadUntilSettled = ({
  isSettling,
  onSettle,
  reread,
  maxMs,
}: {
  isSettling: boolean
  onSettle: () => unknown
  reread: () => unknown
  maxMs: number
}) => {
  const callbacksRef = useRef({ onSettle, reread })
  callbacksRef.current = { onSettle, reread }
  useEffect(() => {
    if (!isSettling) {
      return
    }
    callbacksRef.current.onSettle()
    let timer: ReturnType<typeof setTimeout> | undefined
    const rereadAfter = (delayMs: number) => {
      timer = setTimeout(() => {
        callbacksRef.current.reread()
        if (delayMs * 2 < maxMs) {
          rereadAfter(delayMs * 2)
        }
      }, delayMs)
    }
    rereadAfter(SETTLING_REREAD_MS)
    return () => clearTimeout(timer)
  }, [isSettling, maxMs])
}
